import {
  Center,
  Loader,
  Pagination,
  Pill,
  SimpleGrid,
  Table,
  Title,
  Text,
  Tooltip,
} from "@mantine/core";
import { useQuery } from "@apollo/client";
import { PropsWithChildren, useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { parseDateOnly } from "shared/src/utils/dateUtils";
import { gql } from "@/__generated__";
import {
  Maybe,
  ProviderReferralsQuery,
  ReferredAppealStatus,
  SponsoringParty,
} from "@/__generated__/graphql";
import CheckboxIndicator from "../CheckboxIndicator";
import { ReferralActionsDropdown } from "../ReferralActionsDropdown";
import { AppealOutcomeUpdateModal } from "../AppealOutcomeUpdateModal";
import { SearchBar } from "../SearchBar";
import { useSearch } from "../../hooks/useSearch";
import InlineBanner from "../shared/InlineBanner";
import {
  AppealState,
  getAppealStateFromAppealStatus,
} from "../../types/appealState";

import styles from "./referral-table.module.scss";
import { useProviderHasFacilities } from "@/hooks/useProviderHasFacilities.ts";
import { useIsEnterpriseProvider } from "@/hooks/useIsEnterpriseProvider.ts";

export const GET_REFERRALS = gql(`
  query ProviderReferrals($limit: Int, $offset: Int, $filter: ReferralsFilterInput) {
    referrals(limit: $limit, offset: $offset, filter: $filter) {
      referrals {
        referralId
        patientEmail
        referringUser {
          providerUserId
          firstName
          lastName
          email
        }
        appeal {
          appealId
          referringProviderName
          createdTimestamp
          updatedTimestamp
          sponsoringParty
          sponsorshipExpiredTimestamp
          status {
            referred
            insuranceCardUpload
            insuranceCardUploadSkipped
            paymentSuccessful
            issueOutstanding
            denialUpheld
            denialOverturned
            appealSubmitted
          }
          patient {
            firstName
            lastName
            dateOfBirth
          }
          referringFacilityName,
        }
      }
      totalCount
    }
    provider {
      facilities {
        providerFacilityId
      }
    }
  }
`);

const PAGE_SIZE = 20;

type IndicatorTooltipProps = {
  sponsoringParty: SponsoringParty;
};

const IndicatorTooltip = ({
  sponsoringParty,
  children,
}: PropsWithChildren<IndicatorTooltipProps>) => (
  <Tooltip
    label={`Sponsored by ${sponsoringParty === SponsoringParty.Claimable ? "Claimable" : "your practice"}`}
    withArrow
    arrowSize={16}
  >
    {children}
  </Tooltip>
);

type SponsoredAppealIndicatorProps = {
  sponsoringParty?: Maybe<SponsoringParty>;
  isExpired: boolean;
  isAppealSubmitted: boolean;
};

const SponsoredAppealIndicator = ({
  sponsoringParty,
  isExpired,
  isAppealSubmitted,
}: SponsoredAppealIndicatorProps) => {
  if (!sponsoringParty) {
    return null;
  }

  if (isExpired) {
    return <CheckboxIndicator variant="x" />;
  }

  if (!isAppealSubmitted) {
    return (
      <IndicatorTooltip sponsoringParty={sponsoringParty}>
        <CheckboxIndicator variant="disabled-check" />
      </IndicatorTooltip>
    );
  }

  return (
    <IndicatorTooltip sponsoringParty={sponsoringParty}>
      <CheckboxIndicator variant="check" />
    </IndicatorTooltip>
  );
};

type ReferralTableProps = {
  showTitle?: boolean;
};

const ReferralTable = ({ showTitle }: ReferralTableProps) => {
  const [activePage, setPage] = useState(1);
  const [referralsByPage, setReferralsByPage] = useState<
    ProviderReferralsQuery["referrals"]["referrals"][]
  >([]);
  const providerHasFacilities = useProviderHasFacilities();
  const isEnterpriseProvider = useIsEnterpriseProvider();
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [selectedAppealId, setSelectedAppealId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const navigate = useNavigate();
  const isRowNavigationEnabled = useFeatureFlagEnabled(
    "appeal-hub-referral-flow-v2",
  );

  const {
    searchCriteria,
    isSearchModalOpen,
    openSearchModal,
    closeSearchModal,
    handleSearch: updateSearchCriteria,
    clearAllSearch,
    hasActiveSearch,
  } = useSearch();

  const [statusFilter, setStatusFilter] = useState<ReferredAppealStatus[]>([]);

  const filter = useMemo(() => {
    const baseFilter: {
      patientFirstName?: string;
      patientLastName?: string;
      patientDateOfBirth?: string;
      status?: ReferredAppealStatus[];
    } = {};

    if (searchCriteria.patientFirstName) {
      baseFilter.patientFirstName = searchCriteria.patientFirstName;
    }
    if (searchCriteria.patientLastName) {
      baseFilter.patientLastName = searchCriteria.patientLastName;
    }
    if (searchCriteria.patientDateOfBirth) {
      baseFilter.patientDateOfBirth = searchCriteria.patientDateOfBirth;
    }
    if (statusFilter.length > 0) {
      baseFilter.status = statusFilter;
    }

    return Object.keys(baseFilter).length > 0 ? baseFilter : undefined;
  }, [searchCriteria, statusFilter]);

  const { loading, error, fetchMore, data } = useQuery(GET_REFERRALS, {
    fetchPolicy: "no-cache",
    variables: {
      limit: PAGE_SIZE,
      offset: 0,
      filter,
    },
  });

  useEffect(() => {
    if (data) {
      setReferralsByPage([data.referrals.referrals]);
    }
  }, [data]);

  useEffect(() => {
    setPage(1);
    setReferralsByPage([]);
    fetchMore({
      variables: {
        limit: PAGE_SIZE,
        offset: 0,
        filter,
      },
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    }).then((result) => {
      setReferralsByPage([result.data.referrals.referrals]);
    });
  }, [filter, fetchMore]);

  const handleStatusFilterChange = (statuses: AppealState[]) => {
    const mappedStatuses: ReferredAppealStatus[] = [];

    statuses.forEach((status) => {
      switch (status) {
        case AppealState.Referred:
          mappedStatuses.push(ReferredAppealStatus.Referred);
          break;
        case AppealState.ReferralInProgress:
          mappedStatuses.push(ReferredAppealStatus.ReferralInProgress);
          break;
        case AppealState.InProgress:
          mappedStatuses.push(ReferredAppealStatus.InProgress);
          break;
        case AppealState.InSupport:
          mappedStatuses.push(ReferredAppealStatus.InSupport);
          break;
        case AppealState.Submitted:
          mappedStatuses.push(ReferredAppealStatus.Submitted);
          break;
        case AppealState.NotApproved:
          mappedStatuses.push(ReferredAppealStatus.NotApproved);
          break;
        case AppealState.Approved:
          mappedStatuses.push(ReferredAppealStatus.Approved);
          break;
        case AppealState.Expired:
          mappedStatuses.push(ReferredAppealStatus.Expired);
          break;
      }
    });

    setStatusFilter(mappedStatuses);
  };

  const handleResetFilters = () => {
    setStatusFilter([]);
  };

  const handleClearSearch = () => {
    clearAllSearch();
  };

  const currentAppealStates = useMemo(() => {
    const states: AppealState[] = [];
    statusFilter.forEach((status) => {
      switch (status) {
        case ReferredAppealStatus.Referred:
          states.push(AppealState.Referred);
          break;
        case ReferredAppealStatus.ReferralInProgress:
          states.push(AppealState.ReferralInProgress);
          break;
        case ReferredAppealStatus.InProgress:
          states.push(AppealState.InProgress);
          break;
        case ReferredAppealStatus.InSupport:
          states.push(AppealState.InSupport);
          break;
        case ReferredAppealStatus.Submitted:
          states.push(AppealState.Submitted);
          break;
        case ReferredAppealStatus.NotApproved:
          states.push(AppealState.NotApproved);
          break;
        case ReferredAppealStatus.Approved:
          states.push(AppealState.Approved);
          break;
        case ReferredAppealStatus.Expired:
          states.push(AppealState.Expired);
          break;
      }
    });
    return states;
  }, [statusFilter]);

  const refreshCurrentPage = async () => {
    const result = await fetchMore({
      variables: {
        offset: (activePage - 1) * PAGE_SIZE,
        filter,
      },
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });

    const updatedReferralsByPage = [...referralsByPage];
    updatedReferralsByPage[activePage - 1] = result.data.referrals.referrals;
    setReferralsByPage(updatedReferralsByPage);
  };

  const handleUpdateAppealOutcome = (
    referral: ProviderReferralsQuery["referrals"]["referrals"][number],
  ) => {
    setSelectedAppealId(referral.appeal.appealId);
  };

  const handleModalSuccess = refreshCurrentPage;

  const handleDownloadError = (errorMessage: string) => {
    setDownloadError(errorMessage);
  };

  const handleRowClick = (appealId: string) => {
    if (!isRowNavigationEnabled) return;
    navigate(`/appeals/${appealId}/strengthen/patient`);
  };

  const handleRowKeyDown = (
    event: React.KeyboardEvent<HTMLTableRowElement>,
    appealId: string,
  ) => {
    if (!isRowNavigationEnabled) return;
    if (event.key === "Enter") {
      navigate(`/appeals/${appealId}/strengthen/patient`);
    }
  };

  const appealStateToPill = (state: AppealState) => {
    let rootStyle;
    switch (state) {
      case AppealState.Submitted: {
        rootStyle = styles.submitted;
        break;
      }
      case AppealState.NotApproved: {
        rootStyle = styles.notApproved;
        break;
      }
      case AppealState.Referred: {
        rootStyle = styles.referred;
        break;
      }
      case AppealState.ReferralInProgress: {
        rootStyle = styles.referralInProgress;
        break;
      }
      case AppealState.Expired: {
        rootStyle = styles.expired;
        break;
      }
      case AppealState.Approved: {
        rootStyle = styles.approved;
        break;
      }
      case AppealState.InSupport: {
        rootStyle = styles.support;
        break;
      }
      default: {
        rootStyle = styles.inProgress;
        break;
      }
    }

    return (
      <Pill
        classNames={{
          label: styles.statusLabel,
          root: rootStyle,
        }}
      >
        {state.toString()}
      </Pill>
    );
  };

  const canDownloadAppeal = (appealState: AppealState) => {
    return [
      AppealState.Approved,
      AppealState.Submitted,
      AppealState.NotApproved,
    ].includes(appealState);
  };

  const canUpdateAppealOutcome = (appealState: AppealState) => {
    return [
      AppealState.Submitted,
      AppealState.Approved,
      AppealState.NotApproved,
    ].includes(appealState);
  };

  const rows = (referrals: ProviderReferralsQuery["referrals"]["referrals"]) =>
    referrals.map((referral) => {
      const appealState: AppealState = getAppealStateFromAppealStatus(
        referral.appeal.status,
        Boolean(referral.appeal.sponsorshipExpiredTimestamp),
      );
      return (
        <Table.Tr
          key={referral.referralId}
          onClick={
            isRowNavigationEnabled
              ? () => handleRowClick(referral.appeal.appealId)
              : undefined
          }
          onKeyDown={
            isRowNavigationEnabled
              ? (e) => handleRowKeyDown(e, referral.appeal.appealId)
              : undefined
          }
          tabIndex={isRowNavigationEnabled ? 0 : undefined}
          className={isRowNavigationEnabled ? styles.clickableRow : undefined}
        >
          <Table.Td>
            <SimpleGrid cols={1} verticalSpacing={0}>
              <Text
                className={styles.patientName}
              >{`${referral.appeal.patient?.firstName} ${referral.appeal.patient?.lastName}`}</Text>
              <Text className={styles.patientEmail}>
                {referral.patientEmail}
              </Text>
            </SimpleGrid>
          </Table.Td>
          <Table.Td>
            {referral.appeal.patient?.dateOfBirth
              ? format(
                  parseDateOnly(referral.appeal.patient.dateOfBirth),
                  "MM/dd/yyyy",
                )
              : "—"}
          </Table.Td>
          <Table.Td>
            {referral.referringUser ? (
              referral.referringUser.firstName &&
              referral.referringUser.lastName ? (
                <SimpleGrid cols={1} verticalSpacing={0}>
                  <Text
                    className={styles.referringUserName}
                  >{`${referral.referringUser.firstName} ${referral.referringUser.lastName}`}</Text>
                  <Text className={styles.referringUserEmail}>
                    {referral.referringUser.email}
                  </Text>
                </SimpleGrid>
              ) : (
                <Text className={styles.referringUserEmail}>
                  {referral.referringUser.email}
                </Text>
              )
            ) : (
              <em>{"—"}</em>
            )}
          </Table.Td>
          <Table.Td>
            {format(new Date(referral.appeal.createdTimestamp), "MM/dd/yyyy")}
          </Table.Td>
          <Table.Td>
            {format(new Date(referral.appeal.updatedTimestamp), "MM/dd/yyyy")}
          </Table.Td>
          {providerHasFacilities && (
            <Table.Td>
              {referral.appeal.referringFacilityName || <em>{"—"}</em>}
            </Table.Td>
          )}
          {!isEnterpriseProvider && (
            <Table.Td align="center">
              <SponsoredAppealIndicator
                sponsoringParty={referral.appeal.sponsoringParty}
                isAppealSubmitted={Boolean(
                  referral.appeal.status.appealSubmitted,
                )}
                isExpired={Boolean(referral.appeal.sponsorshipExpiredTimestamp)}
              />
            </Table.Td>
          )}
          <Table.Td>{appealStateToPill(appealState)}</Table.Td>
          <Table.Td
            className={styles.actionsCell}
            onClick={(e) => e.stopPropagation()}
          >
            <ReferralActionsDropdown
              appeal={referral.appeal}
              onUpdateOutcome={() => handleUpdateAppealOutcome(referral)}
              onDownloadError={handleDownloadError}
              canDownload={canDownloadAppeal(appealState)}
              canUpdateOutcome={canUpdateAppealOutcome(appealState)}
            />
          </Table.Td>
        </Table.Tr>
      );
    });

  return (
    <div className={styles.tableContainer}>
      {showTitle && (
        <Title order={1} className={styles.center}>
          {"My Appeals"}
        </Title>
      )}
      {downloadError && (
        <InlineBanner
          type="error"
          message={downloadError}
          dismissable
          onDismiss={() => setDownloadError(null)}
        />
      )}
      {(loading || fetchMoreLoading) && <Loader color="black" />}
      {!loading &&
        !error &&
        !fetchMoreLoading &&
        data &&
        data.referrals.totalCount === 0 &&
        "Start referring patients to track their appeals here"}
      {!loading && !error && !fetchMoreLoading && (
        <>
          <SearchBar
            onStatusFilterChange={handleStatusFilterChange}
            onResetFilters={handleResetFilters}
            onSearch={updateSearchCriteria}
            onSearchClear={handleClearSearch}
            searchCriteria={searchCriteria}
            hasActiveSearch={hasActiveSearch}
            isSearchModalOpen={isSearchModalOpen}
            onOpenSearchModal={openSearchModal}
            onCloseSearchModal={closeSearchModal}
            selectedStatuses={currentAppealStates}
          />
          <Table
            highlightOnHover
            classNames={{ table: styles.table, td: styles.td }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{"Patient"}</Table.Th>
                <Table.Th>{"Date of Birth"}</Table.Th>
                <Table.Th>{"Referred By"}</Table.Th>
                <Table.Th>{"Referral Sent"}</Table.Th>
                <Table.Th>{"Last Activity"}</Table.Th>
                {providerHasFacilities && <Table.Th>{"Facility"}</Table.Th>}
                {!isEnterpriseProvider && (
                  <Table.Th className={styles.centerTH}>{"Sponsored"}</Table.Th>
                )}
                <Table.Th>{"Appeal Status"}</Table.Th>
                <Table.Th className={styles.actionsColumn}>
                  {"Actions"}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            {data &&
              data.referrals.totalCount > 0 &&
              referralsByPage.length > 0 && (
                <Table.Tbody>
                  {rows(referralsByPage[activePage - 1])}
                </Table.Tbody>
              )}
          </Table>
          <Center>
            <Pagination
              size="sm"
              color="black"
              className={styles.pagination}
              value={activePage}
              onChange={async (page) => {
                if (!referralsByPage[page - 1]?.length) {
                  setFetchMoreLoading(true);

                  const result = await fetchMore({
                    variables: {
                      offset: (page - 1) * PAGE_SIZE,
                      filter,
                    },
                    updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
                  });
                  const updatedReferralsByPage = [...referralsByPage];
                  updatedReferralsByPage[page - 1] =
                    result.data.referrals.referrals;

                  setReferralsByPage(updatedReferralsByPage);
                  setFetchMoreLoading(false);
                }
                setPage(page);
              }}
              total={Math.ceil((data?.referrals.totalCount ?? 0) / PAGE_SIZE)}
            />
          </Center>
        </>
      )}

      {selectedAppealId && (
        <AppealOutcomeUpdateModal
          onClose={() => {
            setSelectedAppealId(null);
          }}
          appealId={selectedAppealId}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default ReferralTable;
